import { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import type { ActiveElement, ChartDataset, ChartEvent, TooltipItem } from 'chart.js';

interface InstructorRadarChartDataset {
  backgroundColor: string;
  borderColor: string;
  borderDash?: number[];
  borderWidth: number;
  data: number[];
  label: string;
  pointBackgroundColor: string;
  pointHoverRadius: number;
  pointRadius: number;
}

interface InstructorRadarChartProps {
  animationDuration?: number;
  className?: string;
  datasets: InstructorRadarChartDataset[];
  emptyMessage?: string;
  height?: number | string;
  labels: string[];
  maxScore?: number;
  onPointClick?: (index: number) => void;
  pointLabelFontSize?: number;
  tickFontSize?: number;
  tooltipDecimals?: number | null;
}

const formatHeight = (height: number | string) =>
  typeof height === 'number' ? `${height}px` : height;

const formatTooltipValue = (
  rawValue: unknown,
  maxScore: number,
  decimals: number | null
) => {
  const value = Number(rawValue);

  if (!Number.isFinite(value)) {
    return `0/${maxScore}`;
  }

  if (decimals === null) {
    return `${value}/${maxScore}`;
  }

  return `${value.toFixed(decimals)}/${maxScore}`;
};

const InstructorRadarChart = ({
  animationDuration = 300,
  className = '',
  datasets,
  emptyMessage = 'Select at least two dimensions.',
  height = '100%',
  labels,
  maxScore = 3,
  onPointClick,
  pointLabelFontSize = 10,
  tickFontSize = 9,
  tooltipDecimals = 2,
}: InstructorRadarChartProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas || labels.length === 0 || datasets.length === 0) {
      return undefined;
    }

    const chartDatasets: ChartDataset<'radar', number[]>[] = datasets.map(
      (dataset) => ({
        backgroundColor: dataset.backgroundColor,
        borderColor: dataset.borderColor,
        borderDash: dataset.borderDash,
        borderWidth: dataset.borderWidth,
        data: dataset.data,
        label: dataset.label,
        pointBackgroundColor: dataset.pointBackgroundColor,
        pointHoverRadius: dataset.pointHoverRadius,
        pointRadius: dataset.pointRadius,
      })
    );

    const chart = new Chart(canvas, {
      data: {
        datasets: chartDatasets,
        labels,
      },
      options: {
        animation: {
          duration: animationDuration,
        },
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context: TooltipItem<'radar'>) => {
                const label = context.dataset.label || 'Score';
                return `${label}: ${formatTooltipValue(
                  context.raw,
                  maxScore,
                  tooltipDecimals
                )}`;
              },
            },
          },
        },
        onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
          const selectedElement = elements[0];
          if (!onPointClick || !selectedElement) {
            return;
          }

          onPointClick(selectedElement.index);
        },
        responsive: true,
        scales: {
          r: {
            angleLines: {
              color: '#EBEBEB',
            },
            grid: {
              color: '#EBEBEB',
            },
            max: maxScore,
            min: 0,
            pointLabels: {
              color: '#546A7B',
              font: {
                family: 'DM Sans',
                size: pointLabelFontSize,
                weight: 500,
              },
            },
            ticks: {
              backdropColor: 'transparent',
              color: '#94A3B8',
              font: {
                size: tickFontSize,
              },
              stepSize: 1,
            },
          },
        },
      },
      type: 'radar',
    });

    return () => {
      chart.destroy();
    };
  }, [
    animationDuration,
    datasets,
    labels,
    maxScore,
    onPointClick,
    pointLabelFontSize,
    tickFontSize,
    tooltipDecimals,
  ]);

  if (labels.length < 2 || datasets.length === 0) {
    return <div className="instructor-mini-radar-empty">{emptyMessage}</div>;
  }

  return (
    <div
      className={`instructor-chartjs-radar ${className}`.trim()}
      style={{ height: formatHeight(height) }}
    >
      <canvas ref={canvasRef} />
    </div>
  );
};

export { InstructorRadarChart };
export type { InstructorRadarChartDataset };
